import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard } from 'lucide-react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface PaymentMethodSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPayPal: () => void;
  onSelectRazorpay: () => void;
}

const PaymentMethodSelectModal = ({ isOpen, onClose, onSelectPayPal, onSelectRazorpay }: PaymentMethodSelectModalProps) => {
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[100]"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-primary-dark p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-5 font-semibold">Choose Payment Method</h2>
                    <p className="text-2 text-white/80">Select how you would like to pay</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Payment Options */}
            <div className="p-6 space-y-4">
              {/* Razorpay - Available */}
              <button
                type="button"
                onClick={onSelectRazorpay}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-primary/50 hover:bg-primary/5 transition-all text-left group"
              >
                <div className="w-12 h-12 rounded-lg bg-[#0C2451]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0C2451]/20 transition-colors">
                  <span className="text-4 font-semibold text-[#0C2451]">R</span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Pay using Razorpay</p>
                  <p className="text-2 text-gray-500">UPI, Cards, Net Banking & Wallets</p>
                </div>
              </button>

              {/* PayPal - Available */}
              <button
                type="button"
                onClick={onSelectPayPal}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-primary/50 hover:bg-primary/5 transition-all text-left group"
              >
                <div className="w-12 h-12 rounded-lg bg-[#003087]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#003087]/20 transition-colors">
                  <svg className="h-6 w-6" viewBox="0 0 24 24">
                    <path fill="#003087" d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .76-.646h6.4c2.9 0 5.1.9 6.4 2.6 1.3 1.7 1.6 4 1 6.9-.6 2.9-2 5.2-4.2 6.9-2.2 1.7-5 2.6-8.4 2.6H9.4l-.9 5.3a.641.641 0 0 1-.633.54l-1.1.07-.7 4.2z" />
                    <path fill="#009CDE" d="M20.6 7.3c.4 2 .2 3.7-.5 5.1-.7 1.4-1.8 2.5-3.2 3.2-1.4.7-3 .9-4.9.6l-.7-.1h-.5c-.3 0-.5.2-.6.5l-1 6-1.1.1H5.5a.77.77 0 0 1-.76-.65L2.5 3.1a.77.77 0 0 1 .76-.9h6.4c1.5 0 2.8.4 3.8 1.2 1 .8 1.6 1.9 1.9 3.2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Pay using PayPal</p>
                  <p className="text-2 text-gray-500">Pay securely with your PayPal account</p>
                </div>
              </button>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <button
                onClick={onClose}
                className="w-full px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PaymentMethodSelectModal;
